import { signal, type ReadonlySignal, type Signal } from "@preact/signals";
import type { ComponentChild } from "preact";

/**
 * Placement mode for a pane. Chosen at creation time and immutable
 * thereafter — panes cannot switch placement after they're opened.
 */
export type PanePlacement = "fullscreen" | "side" | "floating";

/**
 * A pane title: either a plain string, or a render function (like `header`)
 * rendered as a component in the header so it can use hooks (i18n, signals).
 */
export type PaneTitle = string | (() => ComponentChild);

/**
 * Why a pane closed, passed to its `onClose` handler so consumers can react
 * differently to an explicit dismissal vs. the system taking the pane away:
 * - "user": the viewer clicked the pane header's close (X) button.
 * - "displaced": the pane was closed to make room for another pane opening (a
 *   fullscreen/mobile pane closes all others; a new side pane replaces the old).
 * - "programmatic": closed by an explicit `closePane`/`closeAll`/
 *   `closeFullscreenPanes` call (e.g. navigation revealing the reader).
 */
export type PaneCloseReason = "user" | "displaced" | "programmatic";

export interface Pane {
  /** Stable pane identifier. */
  id: string;
  /** Title shown in the pane's header. */
  title: PaneTitle;
  /** Custom component rendered in this pane. */
  component: () => ComponentChild;
  /**
   * Optional icon rendered before the title in the pane's header. Rendered as
   * a component so it can use hooks.
   */
  icon?: () => ComponentChild;
  /**
   * Optional content rendered at the very start of the pane's header, before
   * the icon and title — a pane's own back button, for instance. Rendered as a
   * component so it can use hooks (i18n, signals).
   */
  leading?: () => ComponentChild;
  /**
   * Optional custom header content rendered inside the pane's header, between
   * the title and the close button. Rendered as a component so it can use
   * hooks (i18n, signals). Omit for a plain title-and-close header.
   */
  header?: () => ComponentChild;
  /**
   * Optional callback invoked once when the pane closes, by any path: the
   * header's close (X) button, a programmatic `closePane`/`closeAll`/
   * `closeFullscreenPanes`, or displacement when another pane opens. Receives
   * why it closed (see `PaneCloseReason`) so a handler can distinguish an
   * explicit user dismissal from the system taking the pane away. Use it to
   * sync external state that mirrors the pane's open/closed status.
   */
  onClose?: (reason: PaneCloseReason) => void;
  /**
   * Optional guard consulted before the pane closes via the header's close
   * (X) button, before anything else happens. Returning `false` blocks that
   * close — the guard is then responsible for closing the pane itself (e.g.
   * after the user confirms in a modal it opens), typically via `closePane`.
   * Returning `true` (or omitting `confirmClose` entirely) lets the close
   * proceed immediately, same as today. Only consulted for the header's
   * close button — a programmatic `closePane`/`closeAll`/
   * `closeFullscreenPanes` call, or displacement when another pane opens,
   * bypasses it entirely, since those already represent a decision made
   * elsewhere and blocking them on an unrelated confirmation would surprise
   * the caller.
   */
  confirmClose?: () => boolean;
  /** Placement mode, fixed at creation time. */
  placement: PanePlacement;
  /** Pane X position for floating placement. */
  x: number;
  /** Pane Y position for floating placement. */
  y: number;
  /** Pane width (floating), or side-panel width (side placement). */
  width: number;
  /** Pane height (floating placement only). */
  height: number;
}

export interface PaneOpenOptions {
  /** Placement mode for the new pane. Immutable after creation. */
  placement: PanePlacement;

  /**
   * Title shown in the pane's header. Either a plain string, or a render
   * function (like `header`) rendered as a component so it can use hooks
   * (i18n, signals) — e.g. for a translated or reactive title.
   */
  title: PaneTitle;
  /** Custom component rendered in the pane. */
  component: () => ComponentChild;
  /**
   * Optional icon rendered before the title in the pane's header. Rendered as
   * a component so it can use hooks.
   */
  icon?: () => ComponentChild;
  /**
   * Optional content rendered at the very start of the pane's header, before
   * the icon and title — a pane's own back button, for instance. Rendered as a
   * component so it can use hooks (i18n, signals).
   */
  leading?: () => ComponentChild;
  /**
   * Optional custom header content rendered inside the pane's header, between
   * the title and the close button. Rendered as a component so it can use
   * hooks (i18n, signals). Omit for a plain title-and-close header.
   */
  header?: () => ComponentChild;
  /**
   * Optional callback invoked once when the pane closes, by any path: the
   * header's close (X) button, a programmatic `closePane`/`closeAll`/
   * `closeFullscreenPanes`, or displacement when another pane opens. Receives
   * why it closed (see `PaneCloseReason`) so a handler can distinguish an
   * explicit user dismissal from the system taking the pane away. Use it to
   * sync external state that mirrors the pane's open/closed status.
   */
  onClose?: (reason: PaneCloseReason) => void;
  /**
   * Optional guard consulted before the pane closes via the header's close
   * (X) button. See the identical field on `Pane` for the full contract.
   */
  confirmClose?: () => boolean;
  /**
   * Optional stable pane identifier.
   * When provided, an existing pane with this ID is reused and updated with
   * the given title/component (placement is not changed). If no pane with
   * this ID exists, a new pane with this ID is created.
   */
  id?: string;
  /**
   * Optional starting size in pixels, defaulting to 480x320. Side panes use
   * only the width — their height is the height of the reader area. Applied
   * when the pane is first created: reopening a pane by `id` keeps whatever
   * size the user dragged it to.
   */
  size?: { width: number; height: number };
  /**
   * When true, opening (or reusing) this pane closes every other pane —
   * including fullscreen ones — so it is the only pane left open, the same
   * way a fullscreen pane does. Each displaced pane's `onClose` receives
   * `"displaced"`. Not stored on the pane: it applies to this open only.
   */
  exclusive?: boolean;
}

export interface PanesManager {
  /** All panes currently open. */
  panes: Signal<Pane[]>;

  /** Currently selected pane ID. */
  selectedPaneId: Signal<string | null>;

  /** Selects a pane by ID if it exists. */
  selectPane: (paneId: string) => void;

  /**
   * Opens a new pane, or updates an existing one when `options.id` matches an
   * open pane.
   *
   * Only one pane may fill the screen at a time: opening (or reusing) a
   * `"fullscreen"` pane — or opening any pane while on mobile, where every
   * pane is displayed fullscreen — closes all other panes first, leaving just
   * the new/reused pane. Only one `"side"` pane may be open at a time; opening
   * a new one closes the existing side pane first. `"floating"` panes
   * otherwise coexist, stacked by open/selection order. `options.exclusive`
   * closes all other panes regardless of placement.
   */
  openPane: (options: PaneOpenOptions) => Pane;

  /**
   * Closes a pane. Returns true when a pane was closed. `reason` is forwarded
   * to the pane's `onClose` handler and defaults to `"programmatic"`; the
   * header's close button passes `"user"`.
   */
  closePane: (paneId: string, reason?: PaneCloseReason) => boolean;

  /** Closes all panes. */
  closeAll: () => void;

  /**
   * Closes every pane currently filling the reader area — `"fullscreen"` panes
   * on desktop, and (since mobile displays every pane fullscreen) all panes on
   * mobile. Used to reveal the reader when the user navigates to a new
   * location. No-op when nothing is filling the screen.
   */
  closeFullscreenPanes: () => void;

  /** Sets the absolute position (CSS left/top) of a floating pane. */
  setPanePosition: (paneId: string, x: number, y: number) => void;

  /**
   * Resizes a pane by delta values. In side placement only width changes; in
   * floating placement both width and height change; fullscreen panes ignore
   * this call.
   */
  resizePane: (
    paneId: string,
    deltaWidth: number,
    deltaHeight: number,
    uiScale: number
  ) => void;
}

function createPaneFactory() {
  let nextPaneId = 1;

  return ({
    size,
    exclusive: _exclusive,
    ...options
  }: PaneOpenOptions): Pane => {
    const paneId = nextPaneId;
    nextPaneId += 1;
    const offset = (paneId - 1) * 24;

    return {
      ...options,
      id: options.id ?? `pane-${paneId}`,
      x: 48 + offset,
      y: 48 + offset,
      width: size?.width ?? 480,
      height: size?.height ?? 320,
    };
  };
}

/**
 * Creates pane manager state and wiring.
 *
 * Panes are only ever used for custom, non-tab content (e.g. extension tool
 * panels, grid/map portals rendered via `PortalComponent`) — Bible reading
 * tabs live in `TabsLayoutManager` instead.
 */
export function createPanes(isMobile?: ReadonlySignal<boolean>): PanesManager {
  const createPane = createPaneFactory();
  const panes = signal<Pane[]>([]);
  const selectedPaneId = signal<string | null>(null);

  // These command methods read the manager's own signals (`panes`,
  // `selectedPaneId`) exclusively through `.peek()`, never `.value`. A command
  // reads the current state, then writes it — and a read via `.value` inside a
  // caller's reactive scope (effect/computed) would subscribe that caller to
  // the very signal the command then writes. That makes the write re-enter the
  // caller, re-run the command, and preact throws "Cycle detected". `.peek()`
  // reads without subscribing, so invoking any command from an effect never
  // couples that effect to pane state. (`isMobile` is an external, read-only
  // input this manager never writes, so it can't form a cycle and stays a
  // reactive `.value` read.)
  const syncPaneState = (
    nextPanes: Pane[],
    nextSelectedPaneId?: string | null,
    closeReason: PaneCloseReason = "programmatic"
  ) => {
    const prevPanes = panes.peek();
    panes.value = nextPanes;

    const desiredPaneId =
      nextSelectedPaneId !== undefined
        ? nextSelectedPaneId
        : selectedPaneId.peek();
    if (desiredPaneId && nextPanes.some((pane) => pane.id === desiredPaneId)) {
      selectedPaneId.value = desiredPaneId;
    } else {
      selectedPaneId.value = nextPanes[nextPanes.length - 1]?.id ?? null;
    }

    // Notify any pane that just left the list — however it left: the header's
    // close (X) button, a programmatic closePane/closeAll/closeFullscreenPanes,
    // or displacement when another pane opens. This is the single place panes
    // are told they've closed, so an `onClose` handler runs exactly once per
    // removal no matter which path removed the pane, and receives why it closed
    // (`closeReason`). Fired after the signals are updated so the manager is
    // already consistent if a handler re-enters (e.g. calls closePane again — a
    // no-op, since the pane is already gone). Matched by id, so reusing a pane
    // id via openPane (an update, not a close) does not fire it.
    for (const prev of prevPanes) {
      if (!nextPanes.some((pane) => pane.id === prev.id)) {
        prev.onClose?.(closeReason);
      }
    }
  };

  const selectPane = (paneId: string) => {
    if (panes.peek().some((pane) => pane.id === paneId)) {
      selectedPaneId.value = paneId;
    }
  };

  const openPane = (options: PaneOpenOptions): Pane => {
    // A pane fills the whole screen when it's fullscreen, or when we're on a
    // mobile viewport (where every pane is displayed fullscreen). Only one
    // such pane is allowed at a time, so opening one closes all others. An
    // exclusive pane closes all others too, whatever its placement.
    const closesOthers =
      options.exclusive === true ||
      options.placement === "fullscreen" ||
      (isMobile?.value ?? false);

    if (options.id) {
      const existingPane =
        panes.peek().find((pane) => pane.id === options.id) ?? null;
      if (existingPane) {
        const updatedPane: Pane = {
          ...existingPane,
          title: options.title,
          component: options.component,
          icon: options.icon,
          leading: options.leading,
          header: options.header,
          onClose: options.onClose,
          confirmClose: options.confirmClose,
        };
        syncPaneState(
          closesOthers
            ? [updatedPane]
            : panes
                .peek()
                .map((pane) =>
                  pane.id === updatedPane.id ? updatedPane : pane
                ),
          updatedPane.id,
          "displaced"
        );
        return updatedPane;
      }
    }

    // A fullscreen/mobile/exclusive pane closes every other pane; a side pane
    // replaces only the existing side pane (at most one may be open at a time).
    const basePanes = closesOthers
      ? []
      : options.placement === "side"
        ? panes.peek().filter((pane) => pane.placement !== "side")
        : panes.peek();

    const nextPane = createPane(options);
    syncPaneState([...basePanes, nextPane], nextPane.id, "displaced");
    return nextPane;
  };

  const closePane = (
    paneId: string,
    reason: PaneCloseReason = "programmatic"
  ) => {
    if (!panes.peek().some((pane) => pane.id === paneId)) {
      return false;
    }

    syncPaneState(
      panes.peek().filter((pane) => pane.id !== paneId),
      undefined,
      reason
    );
    return true;
  };

  /**
   * Sets the absolute position of a floating pane (in the pane's own CSS
   * coordinate space, i.e. the `left`/`top` values). The caller is
   * responsible for keeping the pane on-screen — the drag handler in
   * PaneLayout clamps against the pane's actual rendered geometry so the
   * result is correct even with the UI `zoom` and the shell's positioned
   * ancestor in play.
   */
  const setPanePosition = (paneId: string, x: number, y: number) => {
    panes.value = panes.peek().map((pane) => {
      if (pane.id !== paneId || pane.placement !== "floating") {
        return pane;
      }

      return {
        ...pane,
        x: Math.max(0, x),
        y: Math.max(0, y),
      };
    });
  };

  const resizePane = (
    paneId: string,
    deltaWidth: number,
    deltaHeight: number,
    uiScale: number
  ) => {
    panes.value = panes.peek().map((pane) => {
      if (pane.id !== paneId) {
        return pane;
      }

      if (pane.placement === "fullscreen") {
        return pane;
      }

      if (pane.placement === "side") {
        return {
          ...pane,
          width: Math.max(320 * uiScale, pane.width + deltaWidth),
        };
      }

      return {
        ...pane,
        width: Math.max(280 * uiScale, pane.width + deltaWidth),
        height: Math.max(180 * uiScale, pane.height + deltaHeight),
      };
    });
  };

  const closeAll = () => {
    syncPaneState([]);
  };

  const closeFullscreenPanes = () => {
    // On mobile every pane is displayed fullscreen (see effectivePanes /
    // openPane's closesOthers), so treat them all as fullscreen there; on
    // desktop only real fullscreen panes fill the reader.
    const remaining =
      (isMobile?.value ?? false)
        ? []
        : panes.peek().filter((pane) => pane.placement !== "fullscreen");
    if (remaining.length === panes.peek().length) {
      return;
    }
    syncPaneState(remaining);
  };

  return {
    panes,
    selectedPaneId,
    selectPane,
    openPane,
    closePane,
    setPanePosition,
    resizePane,
    closeAll,
    closeFullscreenPanes,
  };
}
